/**
 * Meta CAPI용 데이터 정규화 및 해싱 유틸리티
 * tracker/src/utils/hash.ts에서 복사 — Meta 권장 형식으로 정규화 후 SHA-256 해싱
 */

/**
 * SHA-256 해시 함수 (Web Crypto API 사용)
 */
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * 이메일 정규화
 * Meta 권장: 소문자, 공백 제거
 */
export function normalizeEmail(email: string): string {
  if (!email) return '';
  return email.toLowerCase().trim();
}

/**
 * 이메일 해싱 (정규화 + SHA-256)
 */
export async function hashEmail(email: string): Promise<string> {
  const normalized = normalizeEmail(email);
  if (!normalized) return '';
  return await sha256(normalized);
}

/**
 * 전화번호 정규화
 * Meta 권장: 국가 코드 포함, 숫자만, + 기호 제외
 * 예: 010-1234-5678 → 821012345678
 */
export function normalizePhone(phone: string, countryCode: string = '82'): string {
  if (!phone) return '';

  // 숫자만 추출
  const digitsOnly = phone.replace(/\D/g, '');
  if (!digitsOnly) return '';

  // 국가 코드가 이미 포함되어 있으면 그대로 반환
  if (digitsOnly.startsWith(countryCode)) {
    return digitsOnly;
  }

  // 앞의 0 제거 후 국가 코드 추가 (한국 전화번호: 010, 02, 031 등)
  if (digitsOnly.startsWith('0')) {
    return countryCode + digitsOnly.substring(1);
  }

  // 국가 코드 추가
  return countryCode + digitsOnly;
}

/**
 * 전화번호 해싱 (정규화 + SHA-256)
 */
export async function hashPhone(phone: string, countryCode: string = '82'): Promise<string> {
  const normalized = normalizePhone(phone, countryCode);
  if (!normalized) return '';
  return await sha256(normalized);
}

/**
 * 일반 텍스트 정규화
 * Meta 권장: 소문자, 공백 제거
 */
export function normalizeText(text: string): string {
  if (!text) return '';
  return text.toLowerCase().trim();
}

/**
 * 일반 텍스트 해싱 (정규화 + SHA-256)
 */
export async function hashText(text: string): Promise<string> {
  const normalized = normalizeText(text);
  if (!normalized) return '';
  return await sha256(normalized);
}

/**
 * 한국 이름 파싱 (성/이름 분리)
 * Meta API는 fn=first name, ln=last name
 * 한국에서는 성이 앞에 오므로 주의
 */
export function parseKoreanName(fullName: string | undefined): { firstName: string; lastName: string } {
  if (!fullName || fullName.trim().length === 0) {
    return { firstName: '', lastName: '' };
  }

  const name = fullName.trim();

  if (name.length === 2) {
    // 2글자: 첫 글자 성, 둘째 글자 이름
    return {
      firstName: name[1] || '', // 이름
      lastName: name[0] || ''   // 성
    };
  } else if (name.length >= 3) {
    // 3글자 이상: 첫 글자 성, 나머지 이름
    return {
      firstName: name.slice(1) || '', // 이름
      lastName: name[0] || ''         // 성
    };
  } else {
    // 1글자
    return {
      firstName: name,
      lastName: ''
    };
  }
}
